import { useEffect, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Inbox, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import type { IncomingLeadReferral } from '@/gen/cortado/v1/referrals_pb'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ActionGate } from '@/components/shared/action-gate'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { getErrorDescription } from '@/lib/error-utils'
import { reportError } from '@/lib/error-reporting'
import {
  useAcceptLeadReferral,
  useIncomingLeadReferrals,
  useLeadReferralSettings,
  useRejectLeadReferral,
} from '@/stores/lead-referrals.store'
import { useHasLeadReferralsFeature } from '@/hooks/use-has-feature'
import { formatReferralEventDate, referralTimeLeft } from './referral-format'

interface IncomingReferralsPopoverProps {
  organizationId: string
  /** Opens the popover, e.g. when arriving from the bell notification. */
  defaultOpen?: boolean
  /** Called when the popover closes, so the page can drop its open flag. */
  onClose?: () => void
}

/**
 * The leads page's "Referrals" button: referrals other businesses sent here,
 * waiting to be accepted or rejected. Shows only once the business accepts
 * referrals, or while some are waiting, and never without the LeadReferrals
 * feature.
 */
export function IncomingReferralsPopover(props: IncomingReferralsPopoverProps) {
  const { hasFeature } = useHasLeadReferralsFeature()
  if (!hasFeature) return null
  return <IncomingReferralsPopoverContent {...props} />
}

function IncomingReferralsPopoverContent({ organizationId, defaultOpen = false, onClose }: IncomingReferralsPopoverProps) {
  const [open, setOpen] = useState(defaultOpen)
  useEffect(() => {
    if (defaultOpen) setOpen(true)
  }, [defaultOpen])
  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next) onClose?.()
  }
  const { referrals, hasLoaded } = useIncomingLeadReferrals(organizationId)
  const { settings } = useLeadReferralSettings(organizationId)

  if (!settings?.enabled && referrals.length === 0) return null

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="outline" aria-label={`Referrals, ${referrals.length} waiting`}>
          <Inbox className="h-4 w-4" />
          Referrals
          {referrals.length > 0 && (
            <Badge variant="default" size="count" className="ml-1">
              {referrals.length}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[380px]">
        <div className="border-b pb-3">
          <p className="font-medium">Lead referrals</p>
          <p className="text-sm text-muted-foreground">
            Accept to add the lead to your pipeline with the customer's details.
          </p>
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {!hasLoaded ? (
            <p className="py-4 text-sm text-muted-foreground">Loading…</p>
          ) : referrals.length === 0 ? (
            <p className="py-4 text-sm text-muted-foreground">No referrals waiting.</p>
          ) : (
            <ul className="divide-y">
              {referrals.map((referral) => (
                <IncomingReferralItem
                  key={referral.id}
                  organizationId={organizationId}
                  referral={referral}
                  onAccepted={() => handleOpenChange(false)}
                />
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

function IncomingReferralItem({
  organizationId,
  referral,
  onAccepted,
}: {
  organizationId: string
  referral: IncomingLeadReferral
  onAccepted: () => void
}) {
  const navigate = useNavigate()
  const accept = useAcceptLeadReferral()
  const reject = useRejectLeadReferral()
  const [confirmReject, setConfirmReject] = useState(false)
  const busy = accept.isPending || reject.isPending

  const eventType = referral.eventTypeLabel ?? 'Event'
  const details = [
    formatReferralEventDate(referral.firstEventDate, referral.firstEventTimezone),
    referral.totalGuestCount > 0 ? `${referral.totalGuestCount} guests` : null,
    referral.eventsCount > 1 ? `${referral.eventsCount} days` : null,
  ].filter(Boolean)

  const handleAccept = async () => {
    try {
      const resp = await accept.mutateAsync({ organizationId, referralId: referral.id })
      toast.success(`Lead from ${referral.senderOrganizationName} added`)
      onAccepted()
      navigate({ to: '/leads/$id', params: { id: resp.inquiryId } })
    } catch (error) {
      reportError(error, { feature: 'lead_referral_accept', kind: 'mutation' })
      toast.error("Couldn't accept the referral", { description: getErrorDescription(error) })
    }
  }

  const handleReject = async () => {
    if (!confirmReject) {
      setConfirmReject(true)
      return
    }
    try {
      await reject.mutateAsync({ organizationId, referralId: referral.id })
      toast.success('Referral rejected')
    } catch (error) {
      reportError(error, { feature: 'lead_referral_reject', kind: 'mutation' })
      toast.error("Couldn't reject the referral", { description: getErrorDescription(error) })
    }
  }

  return (
    <li className="space-y-2 py-3" data-testid="incoming-referral">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium">{eventType}</p>
          <p className="text-sm text-muted-foreground">from {referral.senderOrganizationName}</p>
        </div>
        <span className="shrink-0 text-xs text-muted-foreground">{referralTimeLeft(referral.expiresAt)}</span>
      </div>
      <p className="text-sm">{details.join(' · ')}</p>
      {referral.note && (
        <p className="whitespace-pre-line rounded-md bg-muted px-3 py-2 text-sm italic text-muted-foreground">
          “{referral.note}”
        </p>
      )}
      <div className="flex justify-end gap-2">
        <ActionGate organizationId={organizationId} permission="LeadsManage" verb="reject" noun="referrals">
          <Button size="sm" variant={confirmReject ? 'destructive' : 'ghost'} onClick={() => void handleReject()} disabled={busy}>
            {reject.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {confirmReject ? 'Confirm reject' : 'Reject'}
          </Button>
        </ActionGate>
        <ActionGate organizationId={organizationId} permission="LeadsManage" verb="accept" noun="referrals">
          <Button size="sm" onClick={() => void handleAccept()} disabled={busy}>
            {accept.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Accept
          </Button>
        </ActionGate>
      </div>
    </li>
  )
}
